/*
  HYA (J-Life, Oct 2026): collection pages load more products as the shopper scrolls.
  - The page links stay in the HTML for search engines and for shoppers without JavaScript;
    this script hides them and keeps the "Showing X of Y products" line up to date.
  - Adds the promo tile from the "HYA · Grid extras" section when that section is on the page.
  - Coming back from a product page reloads the products already seen and returns to the same spot.
*/
(function () {
  'use strict';

  var KEY = 'hya-scroll:';
  var MAX_RESTORE = 10;

  function store() {
    try { return window.sessionStorage; } catch (e) { return null; }
  }

  function HyaScroll(container) {
    this.container = container;
    this.grid = container.querySelector('#product-grid');
    this.status = container.querySelector('[data-hya-scroll]');
    if (!this.grid || !this.status || this.status.dataset.ready) return;
    this.status.dataset.ready = 'true';
    this.total = parseInt(this.status.dataset.total, 10) || 0;
    this.offset = parseInt(this.status.dataset.offset, 10) || 0;
    this.next = this.status.dataset.next || '';
    this.sectionId = this.grid.dataset.id;
    this.count = this.status.querySelector('[data-hya-count]');
    this.fill = this.status.querySelector('[data-hya-fill]');
    this.loader = this.status.querySelector('[data-hya-loader]');
    this.more = this.status.querySelector('[data-hya-more]');
    this.tools = document.querySelector('[data-hya-coltools]');
    this.wide = window.matchMedia('(min-width: 990px)');
    this.canObserve = 'IntersectionObserver' in window;
    this.held = null;
    this.pending = null;
    this.failed = false;
    this.pagesLoaded = 0;
    this.key = KEY + window.location.pathname + window.location.search;
    this.init();
  }

  HyaScroll.prototype.init = function () {
    var self = this;
    this.pagination = this.container.querySelector('.pagination-wrapper');
    if (this.pagination) this.pagination.hidden = true;
    if (this.more && this.tools && this.tools.dataset.label) this.more.textContent = this.tools.dataset.label;
    if (this.more) this.more.addEventListener('click', function () { self.failed = false; self.load(); });
    this.addPromo();
    this.render();
    if (!this.next) return;
    if (this.canObserve) {
      this.observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting && !self.failed) self.load();
      }, { rootMargin: '0px 0px 900px 0px' });
      this.observer.observe(this.status);
    }
    window.addEventListener('pagehide', function () { self.save(); });
    this.restore();
  };

  HyaScroll.prototype.addPromo = function () {
    var template = this.tools && this.tools.querySelector('template[data-hya-promo]');
    if (!template || this.offset > 0 || this.grid.querySelector('.hya-promo-tile')) return;
    var position = parseInt(this.tools.dataset.position, 10) || 4;
    var items = this.grid.querySelectorAll('.grid__item');
    if (items.length <= position) return;
    items[position - 1].after(template.content.cloneNode(true));
    this.holdForRows();
  };

  /* With the promo tile in a 4-column grid, one product waits for the next batch so rows stay full */
  HyaScroll.prototype.holdForRows = function () {
    if (this.next && this.wide.matches && this.grid.querySelector('.hya-promo-tile')) {
      this.held = this.grid.lastElementChild;
      this.held.remove();
    }
  };

  HyaScroll.prototype.shown = function () {
    return this.grid.querySelectorAll('.grid__item:not(.hya-promo-tile)').length;
  };

  HyaScroll.prototype.render = function () {
    var last = this.offset + this.shown();
    if (this.count) this.count.textContent = this.offset > 0 ? (this.offset + 1) + '–' + last : String(last);
    if (this.fill) this.fill.style.width = (this.total ? Math.min(100, Math.round((last / this.total) * 100)) : 100) + '%';
    if (this.more) this.more.hidden = !this.next || (this.canObserve && !this.failed);
    /* If a batch fails to load, the page links come back as well as the Load more button */
    if (this.pagination) this.pagination.hidden = !this.failed;
  };

  HyaScroll.prototype.setBusy = function (busy) {
    if (this.loader) this.loader.hidden = !busy;
    if (this.more) this.more.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  HyaScroll.prototype.load = function () {
    var self = this;
    if (this.pending) return this.pending;
    if (!this.next) return Promise.resolve();
    var url = this.next;
    var join = url.indexOf('?') > -1 ? '&' : '?';
    var view = new URLSearchParams(window.location.search).get('view');
    var extra = view && url.indexOf('view=') === -1 ? '&view=' + encodeURIComponent(view) : '';
    this.setBusy(true);
    this.pending = fetch(url + join + 'section_id=' + encodeURIComponent(this.sectionId) + extra)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var items = doc.querySelectorAll('#product-grid > .grid__item');
        if (!items.length) throw new Error('No products');
        var status = doc.querySelector('[data-hya-scroll]');
        self.next = status ? status.dataset.next || '' : '';
        if (self.held) { self.grid.appendChild(self.held); self.held = null; }
        for (var i = 0; i < items.length; i++) self.grid.appendChild(document.importNode(items[i], true));
        self.holdForRows();
        self.pagesLoaded += 1;
        self.failed = false;
      })
      .catch(function () {
        self.failed = true;
      })
      .then(function () {
        self.pending = null;
        self.setBusy(false);
        self.render();
        self.save();
        if (!self.next && self.observer) {
          self.observer.disconnect();
        } else if (self.observer && !self.failed) {
          /* Re-observing fires the callback again, so a tall screen keeps filling up */
          self.observer.unobserve(self.status);
          self.observer.observe(self.status);
        }
      });
    return this.pending;
  };

  HyaScroll.prototype.save = function () {
    var s = store();
    if (!s) return;
    try { s.setItem(this.key, JSON.stringify({ pages: this.pagesLoaded, y: Math.round(window.scrollY) })); } catch (e) {}
  };

  HyaScroll.prototype.restore = function () {
    var s = store();
    if (!s || (window.Shopify && window.Shopify.designMode)) return;
    var nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    var saved = null;
    try { saved = JSON.parse(s.getItem(this.key) || 'null'); } catch (e) {}
    if (!nav || nav.type !== 'back_forward' || !saved || !saved.pages) return;
    var self = this;
    var target = Math.min(saved.pages, MAX_RESTORE);
    var step = function () {
      var tallEnough = document.documentElement.scrollHeight > (saved.y || 0) + window.innerHeight * 2;
      if (self.pagesLoaded >= target || !self.next || self.failed || tallEnough) {
        window.scrollTo(0, saved.y || 0);
        return;
      }
      return self.load().then(step);
    };
    step();
  };

  function init(root) {
    var containers = (root || document).querySelectorAll('#ProductGridContainer');
    for (var i = 0; i < containers.length; i++) new HyaScroll(containers[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
